import { inject, Injectable } from '@angular/core';
import { navigateFn } from '@softline/application';
import { MessageBarService, modalFn, translator } from '@softline/ui-core';
import type { TemplateDocumentPickerDialogComponent } from '../components/template-document-picker-dialog/template-document-picker-dialog.component';
import { mapDocumentType } from '../helper/document-form/map-document-type';
import { templateDocumentTypes } from '../helper/document-query/template-document.function';
import { SalesDocumentRepository } from '../repositories/sales-document.repository';
import { DocumentType } from '../types/document-type';
import { SalesDocumentService } from './sales-document.service';

export interface DocumentFromTemplateOptions {
  /** Belegarten to offer; defaults to what the sales-document-service can
      create. Types without a template flag are dropped either way. */
  documentTypes?: readonly DocumentType[];
  /** Preselected Belegart, e.g. the overview's selected type. */
  initialDocumentType?: string | null;
}

/** "Neu aus Vorlage": lets the user pick a Musterbeleg and copies it whole
    (`documents/clone`, action `copy`) into a new draft that is opened right
    away. The template itself stays untouched and keeps no link to the copy. */
@Injectable({ providedIn: 'root' })
export class DocumentFromTemplateService {
  private readonly modal = modalFn();
  private readonly t = translator();
  private readonly navigate = navigateFn();
  private readonly messageBar = inject(MessageBarService);
  private readonly documentService = inject(SalesDocumentService);
  private readonly documentRepository = inject(SalesDocumentRepository);

  async create(options: DocumentFromTemplateOptions = {}): Promise<void> {
    const types = templateDocumentTypes(
      options.documentTypes ?? (await this.loadCreatableTypes())
    );
    if (types.length === 0) {
      this.messageBar.info(this.t('#SALES.FROM_TEMPLATE.NO_TYPES'));
      return;
    }
    // lazy: the service sits in the boot-time globals, the dialog does not need to
    const { TemplateDocumentPickerDialogComponent: dialog } =
      await import('../components/template-document-picker-dialog/template-document-picker-dialog.component');
    const picked = await this.modal<TemplateDocumentPickerDialogComponent>({
      component: dialog,
      title: this.t('#SALES.FROM_TEMPLATE.TITLE')(),
      subtitle: this.t('#SALES.FROM_TEMPLATE.SUBTITLE')(),
      icon: 'fa-regular fa-copy',
      dismiss: true,
      class: 'wide',
      data: {
        documentTypes: types,
        initialDocumentType: options.initialDocumentType ?? null,
      },
    });
    if (!picked || picked === 'DISMISSED') return;
    const draft = await this.documentService.cloneDocument(
      picked.sourceId,
      undefined,
      'copy'
    );
    if (!draft) return;
    await this.navigate(['/sales/document', draft.id], { origin: true });
  }

  private async loadCreatableTypes(): Promise<DocumentType[]> {
    const translate = (key: string): string => this.t(key)();
    const types = await this.documentRepository.getDocumentTypes().catch(() => []);
    return types.map((type) => mapDocumentType(type, translate));
  }
}
